import React, { useEffect, useState } from 'react';
import Cal from '@calcom/embed-react';
import { CACHE_TIMES } from 'consts';
import { GetServerSideProps } from 'next';
import Head from 'next/head';

import api from 'api';
import BackLink from 'components/ui/BackLink';
import Block from 'components/ui/Block';
import Container from 'components/ui/Container';
import StaticTextRenderer from 'components/ui/StaticTextRenderer';
import Typography from 'components/ui/Typography';
import { StaticContentProvider, useStaticContent } from 'contexts/StaticContentContext';
import Clock from 'icons/clock.svg';
import { AppLayoutProps } from 'types/AppLayoutProps';
import { StaticContent } from 'types/StaticContent';
import { BookingTypeResponse, PageNumberBookingTypeResponse } from 'types/typefetch';
import { setCacheHeaders } from 'utils';
import getBookingTypes from 'utils/getBookingTypes';
import styles from './boka-tid.module.css';

export const getServerSideProps: GetServerSideProps<Props> = async ctx => {
  const { sanity } = api();
  const [navbarMenu, menuBanner, footerMenu, staticContent] = await Promise.all([
    sanity.navbarMenu(),
    sanity.menuBanner(),
    sanity.footerMenu(),
    sanity.staticContent(),
  ]);

  setCacheHeaders(ctx.res, CACHE_TIMES.BOOK_TIME);

  return {
    props: { footerMenu, menuBanner, navbarMenu, staticContent },
  };
};

export type Props = {
  staticContent: Partial<StaticContent>;
} & AppLayoutProps;

export default function BookPage(props: Props) {
  const [bookingTypes, setBookingTypes] = useState<PageNumberBookingTypeResponse | undefined>(
    undefined,
  );
  const [selectedBooking, setSelectedBooking] = useState<BookingTypeResponse | undefined>(
    undefined,
  );

  useEffect(() => {
    const fetchBookingTypes = async () => {
      const bookingTypes = await getBookingTypes();
      setBookingTypes(bookingTypes);
    };

    fetchBookingTypes();
  }, []);

  return (
    <StaticContentProvider value={props.staticContent}>
      {selectedBooking ? (
        <BookingPage
          booking={selectedBooking}
          removeSelected={() => setSelectedBooking(undefined)}
        />
      ) : (
        <>
          <Head>
            <title>Boka tid - Arkivet Second Hand</title>
          </Head>
          <Block padding="big">
            <Container padding="regular" className={styles.container}>
              <Container padding="none" className={styles.textContainer}>
                <Typography variant="h1" className={styles.title}>
                  Boka tid för klädinlämning
                </Typography>
                <BookingIntroText />
              </Container>
              {bookingTypes?.results?.map(booking => (
                <button
                  key={booking.slug}
                  className={styles.calendar}
                  onClick={() => setSelectedBooking(booking)}
                >
                  <Typography variant="h2" className={styles.name}>
                    {booking.title}
                  </Typography>
                  <div className={styles.badges}>
                    <div className={styles.badge}>
                      <Clock />
                      {booking.duration}m
                    </div>
                  </div>
                </button>
              ))}
            </Container>
          </Block>
        </>
      )}
    </StaticContentProvider>
  );
}

function BookingPage({
  booking,
  removeSelected,
}: {
  booking: BookingTypeResponse;
  removeSelected: () => void;
}) {
  return (
    <>
      <Head>
        <title>Boka tid {booking.title} - Arkivet Second Hand</title>
      </Head>
      <Block padding="big">
        <Container padding="regular" className={styles.calContainer}>
          <div className={styles.backLink}>
            <BackLink onClick={removeSelected} />
          </div>
          <Cal
            namespace={booking.slug}
            calLink={`team/${booking.team_slug}/${booking.slug}`}
            config={{ layout: 'month_view', theme: 'light' }}
            calOrigin={booking.calcom_base_url}
            embedJsUrl={`${booking.calcom_base_url}/embed/embed.js`}
          />
        </Container>
      </Block>
    </>
  );
}

function BookingIntroText() {
  const { bookTimePageText } = useStaticContent();

  return (
    <div className={styles.bookingText}>
      <StaticTextRenderer text={bookTimePageText} />
    </div>
  );
}
